<template>
    <article class="w-full bg-white rounded-lg shadow-lg">
        <div class="p-4 flex flex-col gap-4">
            <div class="flex items-center justify-between">
                <div class="flex-1 flex justify-start items-center gap-3">
                    <div
                        class="w-12 h-12 relative rounded-full overflow-hidden border-2 flex-shrink-0"
                        style="
                            aspect-ratio: 1/1;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                        "
                    >
                        <img
                            v-if="connection.institution"
                            :src="connection.institution.medium_logo_url"
                            :alt="connection.name"
                            class="object-contain h-full w-full"
                        />
                    </div>
                    <div class="flex flex-col justify-center items-start gap-1">
                        <h3
                            class="text-slate-800 text-sm font-medium leading-tight"
                        >
                            {{ computedConnectionName }}
                        </h3>
                        <p
                            class="text-slate-500 text-sm font-normal leading-tight"
                            v-if="connection.connected_at"
                        >
                            Connected at {{ connection.connected_at }}
                        </p>
                    </div>
                </div>
                <div
                    class="flex justify-start items-center sm:gap-6 gap-2 md:flex-row flex-col"
                >
                    <ConnectionStatusBadge
                        :status="connection.connection_status"
                        :connected-date="connection.connected_at"
                        class=""
                    />
                    <div class="flex items-center space-x-2">
                        <ConnectionDropdown
                            @edit-nickname="handleEditNickname"
                            @delete="isRemoveConnectionModalOpen = true"
                        />
                        <button
                            @click="isExpanded = !isExpanded"
                            class="p-2 rounded hover:bg-slate-100 transition-colors flex justify-center items-center active:bg-slate-200 focus:outline-slate-200 focus:border-slate-200 hover:border-slate-300 active:border-slate-300"
                        >
                            <Icon
                                icon="chevron-up"
                                class="transform transition-transform"
                                :class="isExpanded ? 'rotate-180' : ''"
                            />
                        </button>
                    </div>
                </div>
            </div>
            <MemberCardAction :member="connection" />
        </div>
        <div class="border-t border-gray-200"></div>
        <Transition name="list-container">
            <div class="px-4 pb-2" v-show="isExpanded">
                <h4 class="pt-4 pb-1 text-gray-500 text-sm font-medium">
                    Connected cards
                </h4>
                <ul>
                    <li
                        class="flex items-center gap-3"
                        v-if="connection.accounts.length === 0"
                    >
                        <EmptyState
                            description="No connected cards found for this connection."
                            :hasAction="false"
                            containerStyles="py-2"
                        />
                    </li>
                    <li
                        class="py-3 flex items-center gap-3"
                        v-for="account in connection.accounts"
                        :key="account.id"
                    >
                        <div class="w-11 rounded overflow-hidden flex-shrink-0">
                            <ImageLoader>
                                <template #image>
                                    <img
                                        :src="`${cdnUrl}/${account.connected_account.card.card_image.path}/_small/${account.connected_account.card.card_image.file_name}`"
                                        v-if="
                                            account.connected_account &&
                                            account.connected_account.card &&
                                            account.connected_account.card
                                                .card_image
                                        "
                                        class="w-full h-auto object-contain"
                                    />
                                </template>
                                <template #preloader>
                                    <div
                                        class="w-full h-full rounded overflow-hidden flex-shrink-0"
                                    ></div>
                                </template>
                                <template #error>
                                    <img
                                        src="/images/blank_card.png"
                                        class="w-full h-auto object-contain"
                                    />
                                </template>
                            </ImageLoader>
                        </div>
                        <div class="flex items-center gap-1">
                            <div class="flex flex-col">
                                <span
                                    class="text-gray-900 text-sm font-medium leading-tight"
                                >
                                    {{ getAccountName(account) }}
                                    <span
                                        class="text-gray-900 text-sm"
                                        v-if="
                                            account.account_number &&
                                            account.institution_code !==
                                                'citibank'
                                        "
                                    >
                                        {{ `(${account.account_number})` }}
                                    </span>
                                </span>
                                <div class="flex items-center gap-2 mt-1">
                                    <span
                                        @click="
                                            navigateToEditCard(account.guid)
                                        "
                                        class="text-xs bg-amber-50 text-amber-500 px-2 py-1 rounded-full hover:bg-amber-100 transition-colors cursor-pointer inline-flex items-center"
                                    >
                                        <svg
                                            class="w-3 h-3 mr-1"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14M14 4H20M20 4V10M20 4L10 14"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                        Match card
                                    </span>
                                </div>
                            </div>

                            <div
                                :class="'px-3 py-1 rounded-full flex justify-center items-center text-xs font-medium leading-none bg-slate-100 text-slate-500 ml-3 cursor-help'"
                                v-show="account.ignore"
                                v-tooltip="'Card is hidden in your wallet'"
                            >
                                <Icon icon="hidden" class="mr-1" />
                                <span class="hidden sm:block">Hidden</span>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </Transition>
        <ConnectionNicknameModal
            :isOpen="isEditNicknameModalOpen"
            :connection-name="computedConnectionName"
            :connection-guid="props.connection.guid"
            @close="isEditNicknameModalOpen = false"
            @update-success="handleNicknameUpdateSuccess"
        />
        <RemoveConnectionModal
            :isOpen="isRemoveConnectionModalOpen"
            :connection="props.connection"
            @close="isRemoveConnectionModalOpen = false"
            @delete="handleDelete"
        />
    </article>
</template>

<script setup>
import { computed, ref } from 'vue';
import { notify } from 'notiwind';
import Icon from '@/components/ui/icon/icon.vue';
import ConnectionNicknameModal from '@/components/members/connection-nickname-modal.vue';
import ConnectionDropdown from '@/components/members/connection-dropdown.vue';
import ConnectionStatusBadge from '@/components/members/connection-status-badge.vue';
import RemoveConnectionModal from '@/components/members/remove-connection-modal.vue';
import MemberCardAction from '@/components/members/member-card-action.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import { useMembersStore } from '@/store/members';

const props = defineProps({
    connection: Object,
});

const emit = defineEmits([]);
const membersStore = useMembersStore();
const isExpanded = ref(false);
const cdnUrl = ref(window.cdnUrl);
const isEditNicknameModalOpen = ref(false);
const isRemoveConnectionModalOpen = ref(false);
const { deleteMember } = membersStore;

const computedConnectionName = computed(() => {
    const bankName =
        props.connection.institution?.issuing_bank?.name ||
        props.connection.connect_attempt?.bank_name;
    const nickname = props.connection.connect_attempt?.connection_nickname;
    if (nickname) {
        return bankName + ' (' + nickname + ')';
    }
    return bankName || props.connection.name;
});

const getAccountName = (account) => {
    return account.name ? account.name : account.card.title;
};

const navigateToEditCard = (accountGuid) => {
    const storageKey = '10x_temp_account_to_edit';

    const data = {
        guid: accountGuid,
        timestamp: Date.now(),
        source: 'connection_card',
    };

    localStorage.setItem(storageKey, JSON.stringify(data));

    const baseUrl = window.location.origin;
    window.location.href = `${baseUrl}/wallet`;
};

function handleEditNickname() {
    isEditNicknameModalOpen.value = true;
}

function handleNicknameUpdateSuccess(data) {
    if (props.connection.connect_attempt) {
        props.connection.connect_attempt.connection_nickname = data.nickname;
    }
    notify({
        group: 'success',
        title: 'Nickname Updated',
        text: data.nickname
            ? `Connection nickname updated to "${data.nickname}"`
            : 'Connection nickname removed',
    });
}

function handleDelete() {
    deleteMember(props.connection.guid)
        .then(() => {
            isRemoveConnectionModalOpen.value = false;
            notify({
                group: 'success',
                title: 'Connection Deleted',
                text: 'Connection deleted successfully',
            });
        })
        .catch((error) => {
            notify({
                group: 'error',
                title: 'Error Deleting Connection',
                text: 'There was a problem deleting the connection. Please try again.',
            });
            console.error('Error deleting connection:', error);
        });
}
</script>
<style scoped>
.list-container-enter-active,
.list-container-leave-active {
    transition:
        max-height 0.5s ease,
        opacity 0.5s ease;
    max-height: 2000px;
    overflow: hidden;
}

.list-container-enter-from,
.list-container-leave-to {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
}
</style>
